import { useEffect, useMemo, useRef } from "react";
import * as THREE from 'three';
import * as Comlink from 'comlink';
import { useShallow } from "zustand/shallow";
import useEditorStore from "../../../stores/useEditorStore";
import useSliceStore from "../../../stores/tool/useSliceStore";
import usePreparingScanStore from "../../../stores/usePreparingScanStore";
import SlicingWorker from "../../../workers/slicing.worker.js?worker";

export function SlicingManager() {
  const { scene, shadedMesh, material } = useEditorStore(s => s.sceneRefs) ?? {};
  const setIsLoading = useEditorStore(s => s.setIsLoading);
  const [plane, direction, activeSlice, setActiveSlicePieces, clearSlicePreview] = useSliceStore(
    useShallow(s => [s.slicingPlane, s.selectionDirection, s.activeSlice, s.setActiveSlicePieces, s.clearSlicePreview])
  );

  const [selectionPoints, selectedRegion] = usePreparingScanStore(
    useShallow(s => [s.selectionPoints, s.selectedRegion])
  );
  const clearSelection = usePreparingScanStore(s => s.clearSelection);

  // STEP 1: Create refs for slice management and async operation control.
  const slicePiecesRef = useRef(null);
  const workerRef = useRef(null);
  const abortControllerRef = useRef(null); // Cancel in-flight operations

  const previewMaterials = useMemo(() => {
    if (!material) return null;
    const originalMaterial = material;

    // Selected part keeps the original look
    const selected = originalMaterial.clone();
    // selected.color.set('#A16CFF');
    selected.vertexColors = true;

    // selected.transparent = true;
    // selected.opacity = 0.2;

    // Unselected part is slightly transparent and colored
    const unselected = originalMaterial.clone();
    unselected.color.set('#AA0000');
    unselected.transparent = true;
    unselected.opacity = 0.55;
    // You could add an emissive color to make it stand out
    // unselected.emissive = new THREE.Color(0x553377);

    return { selected, unselected };
  }, [material]);

  // EFFECT 1: Perform the slice and manage scene objects using web worker.
  useEffect(() => {
    if (!plane || !shadedMesh || !scene) return;

    // Create abort controller for this effect iteration
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const performSliceAsync = async () => {
      try {
        // Check if operation was aborted before starting
        if (abortController.signal.aborted) return;

        setIsLoading(true, 'Slicing mesh...');

        // Initialize worker if needed
        if (!workerRef.current) {
          const worker = new SlicingWorker();
          workerRef.current = Comlink.wrap(worker);
        }

        const api = workerRef.current;

        // **CRITICAL**: Convert to non-indexed for slicing algorithm
        // The performSlice algorithm iterates positions assuming every 3 vertices form a triangle
        // This is only true for non-indexed geometry. We'll merge vertices back to indexed after slicing.
        const geom = shadedMesh.geometry;
        const sourceGeom = geom.index ? geom.toNonIndexed() : geom;
        const posAttr = sourceGeom.attributes.position;
        const colorAttr = sourceGeom.attributes.color;

        // Create copies for transfer to worker
        const positions = new Float32Array(posAttr.array);
        const colors = colorAttr
          ? new Float32Array(colorAttr.array)
          : null;

        // **CRITICAL**: Dispose temporary non-indexed geometry if we created one
        if (geom.index && sourceGeom !== geom) {
          sourceGeom.dispose();
        }

        // Extract plane data
        // THREE.Plane stores: normal and constant where constant = -normal · coplanarPoint
        const planeNormal = plane.normal.toArray();
        const planeConstant = plane.constant;

        // Call worker to perform slice
        const result = await api.sliceMesh(positions, colors, planeNormal, planeConstant);

        // Check again if aborted before proceeding with scene mutations
        if (abortController.signal.aborted) return;

        if (result && result.upper && result.lower) {
          // Create meshes from worker result (INDEXED geometry)
          const createMeshFromData = (data, name) => {
            if (!data.positions || data.positions.length === 0) return null;

            const geom = new THREE.BufferGeometry();

            // Set position attribute
            geom.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));

            // **CRITICAL**: Set index buffer to preserve vertex topology
            // This maintains proper adjacency relationships for smoothening and other tools
            if (data.indices && data.indices.length > 0) {
              geom.setIndex(new THREE.BufferAttribute(data.indices, 1));
            }

            // **CRITICAL**: Use normals computed in worker after vertex merging
            // These normals are properly averaged across welded vertices at the cut seam
            if (data.normals && data.normals.length > 0) {
              geom.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
            }

            // Set color attribute if present
            if (data.colors && data.colors.length > 0) {
              geom.setAttribute('color', new THREE.BufferAttribute(data.colors, 3));
            }

            // **CRITICAL FIX**: Initialize spatial data structures
            // Normals are already set from worker, only compute bounds
            geom.computeBoundingBox();
            geom.computeBoundingSphere();
            // Note: BVH will be built later if needed by raycasting operations

            const mesh = new THREE.Mesh(geom, material);
            mesh.name = name;
            mesh.matrix.copy(shadedMesh.matrix);
            mesh.matrixAutoUpdate = false;

            return mesh;
          };

          const upperMesh = createMeshFromData(result.upper, shadedMesh.name + '_upper');
          const lowerMesh = createMeshFromData(result.lower, shadedMesh.name + '_lower');

          if (upperMesh && lowerMesh) {
            // Apply preview materials immediately based on current direction
            if (previewMaterials) {
              const { selected, unselected } = previewMaterials;
              upperMesh.material = direction === 'upper' ? selected : unselected;
              lowerMesh.material = direction === 'lower' ? selected : unselected;
            }

            // STEP 2: Store the created meshes in our ref.
            slicePiecesRef.current = { upper: upperMesh, lower: lowerMesh };

            scene.add(upperMesh, lowerMesh);
            setActiveSlicePieces({ upper: upperMesh, lower: lowerMesh });
            shadedMesh.visible = false;
          }
        }
      } catch (error) {
        // Don't log abort errors
        if (error.name !== 'AbortError') {
          console.error('Error slicing mesh:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    performSliceAsync();

    // Cleanup function: abort in-flight operation and remove slices from scene
    return () => {
      // ABORT: Cancel any in-flight async operation to prevent orphaned slices
      abortController.abort();

      // REMOVE: Clean up existing slices from the scene
      if (slicePiecesRef.current) {
        const { upper, lower } = slicePiecesRef.current;

        // Remove from scene
        scene.remove(upper, lower);

        // **CRITICAL FIX**: Dispose BVH trees before disposing geometries
        if (upper.geometry?.boundsTree) {
          upper.geometry.disposeBoundsTree();
        }
        if (lower.geometry?.boundsTree) {
          lower.geometry.disposeBoundsTree();
        }

        // Dispose geometries and materials
        upper.geometry.dispose();
        lower.geometry.dispose();
        upper.material.dispose();
        lower.material.dispose();

        // Clear the ref after cleanup
        slicePiecesRef.current = null;
      }

      // RESTORE: Show original mesh
      if (shadedMesh) {
        shadedMesh.visible = true;
      }
    };
  }, [plane, shadedMesh, scene, setActiveSlicePieces, setIsLoading]);

  // EFFECT 2: Update materials when direction changes (after slices already exist).
  useEffect(() => {
    // activeSlice is used as a trigger to know when new slices are created
    // But we use the ref for actual mesh access (source of truth)
    const slices = slicePiecesRef.current;

    if (slices?.upper && slices?.lower && previewMaterials) {
      const { upper, lower } = slices;
      const { selected, unselected } = previewMaterials;

      upper.material = direction === 'upper' ? selected : unselected;
      lower.material = direction === 'lower' ? selected : unselected;
    }
  }, [activeSlice, direction, previewMaterials]);

  useEffect(() => {

    return () => {
      if(shadedMesh)
        shadedMesh.visible = true;

      clearSelection(); 

      clearSlicePreview();
    }
  }, [clearSelection, clearSlicePreview])

  return null;
}